/**
 * <lazy-video>: een video onder de vouw (PDP-redesign §2.1).
 *
 * - Geen bron in de HTML: de <source> draagt data-src, dus bij het laden van de pagina
 *   wordt alleen de poster opgehaald. Op 200px van het scherm komt de echte src erop.
 * - Gedempt en in een lus afspelen alleen zolang hij voor de helft in beeld is; erbuiten
 *   pauzeren.
 * - De speelknop is voor wie hem echt wil zien: geluid aan, bediening erbij, en dan
 *   stuurt hij zelf (het automatisch pauzeren stopt).
 *
 * Meetplan: pdp_video_play {source: section} bij de eerste start. Buiten een PDP geeft
 * de plaatsing data-product en data-source mee (de homepage: source home).
 */
class LazyVideo extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #near;

  /** @type {IntersectionObserver | undefined} */
  #view;

  /** @type {AbortController | undefined} */
  #controller;

  #started = false;
  #takenOver = false;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;
    this.#controller = new AbortController();

    this.#near = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        this.#near?.disconnect();
        for (const source of video.querySelectorAll('source[data-src]')) {
          source.setAttribute('src', source.getAttribute('data-src') || '');
          source.removeAttribute('data-src');
        }
        video.load();
      },
      { rootMargin: '200px 0px' }
    );
    this.#near.observe(this);

    this.#view = new IntersectionObserver(
      ([entry]) => {
        if (this.#takenOver || !entry) return;
        if (entry.intersectionRatio >= 0.5) {
          video.play()?.then(() => this.#firstPlay()).catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: [0, 0.5] }
    );
    this.#view.observe(this);

    this.querySelector('[data-lazy-video-play]')?.addEventListener(
      'click',
      () => {
        this.#takenOver = true;
        video.muted = false;
        video.controls = true;
        video.play()?.then(() => this.#firstPlay()).catch(() => {});
        this.setAttribute('data-playing', '');
      },
      { signal: this.#controller.signal }
    );
  }

  disconnectedCallback() {
    this.#near?.disconnect();
    this.#view?.disconnect();
    this.#controller?.abort();
  }

  #firstPlay() {
    if (this.#started) return;
    this.#started = true;
    try {
      window.Shopify?.analytics?.publish('pdp_video_play', {
        product: this.dataset.product || location.pathname.match(/\/products\/([^/?#]+)/)?.[1],
        source: this.dataset.source || 'section',
      });
    } catch (error) {
      // tracking mag de video nooit in de weg zitten
    }
  }
}

if (!customElements.get('lazy-video')) {
  customElements.define('lazy-video', LazyVideo);
}
